import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useSortable, SortTh } from "@/components/SortableTable";
import { useAuth } from "@/lib/AuthContext";
import { AccessDenied } from "@/components/AccessDenied";
import HeatChip from "@/components/HeatChip";
import { formatTz, formatTzDate, resolveTz } from "@/lib/timezone";
import { invoicedCell, paidCell, invoiceSortKeys, INVOICE_FILTERS, matchesInvoiceFilter } from "@/lib/proposalInvoiceDisplay";

const STATUSES = ["", "draft", "in_rfp", "sent", "returned", "accepted", "rejected", "expired", "archived"];
const STATUS_COLORS = { draft: "bg-[#F5F5F5] text-[#525252]", in_rfp: "bg-purple-50 text-purple-700", sent: "bg-blue-50 text-blue-700", returned: "bg-[#F0F7FF] text-[#1E3A8A]", accepted: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700", expired: "bg-amber-50 text-amber-700", archived: "bg-[#F5F5F5] text-[#A3A3A3]" };
const fmt = (n, c) => { try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: c || "USD" }).format(n); } catch { return String(n); } };

// Gates use round2(x) !== 0 rather than x > 0 so a credit/rebate
// (negative amount) still appears in the breakdown instead of silently
// vanishing while the headline total quietly shrinks.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Client-side sort keys: heat, plus the INVOICED / PAID columns.
const enrichQuotes = (data) => (data || []).map(q => ({
  ...q,
  heat_rank: q.heat?.days == null ? 99999 : q.heat.days,
  ...invoiceSortKeys(q.invoice),
}));

// One compact two-line cell for the INVOICED / PAID columns. The state
// comes from the linked commercial record, never from proposal status.
function InvoiceStateCell({ cell, testId }) {
  if (cell.quiet) {
    return <span className="text-[#A3A3A3] text-[11px] whitespace-nowrap" title={cell.title} data-testid={testId}>{cell.primary}</span>;
  }
  return (
    <div className="flex flex-col whitespace-nowrap" title={cell.title} data-testid={testId}>
      {cell.tone ? (
        <span className={`self-start px-1.5 py-0.5 text-[10px] font-bold border ${cell.tone}`}>{cell.primary}</span>
      ) : (
        <span className="text-[#525252]">{cell.primary}</span>
      )}
      {cell.secondary && <span className="text-[10px] text-[#A3A3A3] font-mono mt-0.5">{cell.secondary}</span>}
    </div>
  );
}

const totalTooltip = (q) => {
  const b = q.display_breakdown;
  const c = q.currency_code || "USD";
  if (!b) return "";
  const parts = [];
  if (round2(b.monthly_recurring) !== 0) parts.push(`${fmt(b.monthly_recurring, c)}/mo × 12 = ${fmt(b.monthly_recurring_year_1, c)}`);
  if (round2(b.annual_recurring) !== 0) parts.push(`Annual recurring: ${fmt(b.annual_recurring, c)}`);
  if (round2(b.recurring_credits) !== 0) parts.push(`  incl. recurring credits: ${fmt(b.recurring_credits, c)}`);
  if (round2(b.one_off) !== 0) parts.push(`One-off: ${fmt(b.one_off, c)}`);
  if (round2(b.discount) !== 0) parts.push(`Discount: ${fmt(-Number(b.discount || 0), c)}`);
  if (round2(b.tax) !== 0) parts.push(`Tax: ${fmt(b.tax, c)}`);
  parts.push(`──────────`);
  parts.push(`Year-1 Total: ${fmt(q.display_total, c)}`);
  return parts.join("\n");
};


// Inline editor for next_reminder_at. Click pencil → date input. Save
// hits PATCH /api/quotes/{id}/next-reminder. Only shows on proposals
// that are in the right state (status='sent', not accepted/rejected,
// under MAX_REMINDERS=9 reminders sent).
function NextReminderCell({ q, user, onChange }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(() => {
    if (!q.next_reminder_at) return "";
    try { return new Date(q.next_reminder_at).toISOString().slice(0, 16); }
    catch { return ""; }
  });
  const [busy, setBusy] = useState(false);

  const eligible = q.status === "sent" && !q.accepted_at && !q.rejected_at
                   && (q.reminders_sent || 0) < 9;
  if (!eligible) {
    // Render a quiet em-dash so the column lines up but says "n/a".
    return <span className="text-[#E5E5E5]" title="Reminders only apply to active 'sent' proposals">—</span>;
  }

  const save = async () => {
    setBusy(true);
    try {
      const iso = val ? new Date(val).toISOString() : null;
      await api.patch(`/quotes/${q.quote_id}/next-reminder`, { next_reminder_at: iso });
      setEditing(false);
      onChange?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update");
    }
    setBusy(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`next-reminder-edit-${q.quote_id}`}>
        <input
          type="datetime-local"
          value={val}
          onChange={e => setVal(e.target.value)}
          className="border border-[#E5E5E5] px-1.5 py-0.5 text-[11px] font-mono"
          data-testid={`next-reminder-input-${q.quote_id}`}
        />
        <button onClick={save} disabled={busy}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-[#0055FF] text-white disabled:opacity-50"
          data-testid={`next-reminder-save-${q.quote_id}`}>
          {busy ? "…" : "Save"}
        </button>
        <button onClick={() => { setEditing(false); }}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-[#E5E5E5]"
          data-testid={`next-reminder-cancel-${q.quote_id}`}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 group" title="Click pencil to override the next reminder date">
      {q.next_reminder_at ? (
        <div className="flex flex-col">
          <span className="text-[#525252]">{formatTzDate(q.next_reminder_at, resolveTz(user))}</span>
          <span className="text-[10px] text-[#A3A3A3] font-mono">
            {formatTz(q.next_reminder_at, resolveTz(user), { year: undefined, month: undefined, day: undefined })}
          </span>
        </div>
      ) : (
        <span className="text-[#A3A3A3]">Suspended</span>
      )}
      <button
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-[#0055FF] hover:underline ml-1"
        data-testid={`next-reminder-edit-btn-${q.quote_id}`}
      >
        edit
      </button>
    </div>
  );
}


// Inline scheduler for the FIRST send of an un-sent proposal. Saves
// `scheduled_send_at` on the quote so the background dispatcher fires
// `send_quote()` automatically when the time comes.
function ScheduledSendCell({ q, user, onChange }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(() => {
    if (!q.scheduled_send_at) return "";
    try { return new Date(q.scheduled_send_at).toISOString().slice(0, 16); }
    catch { return ""; }
  });
  const [busy, setBusy] = useState(false);

  const eligible = !q.sent_at && ["draft", "in_rfp", "pending_approval"].includes(q.status);
  if (!eligible) {
    return <span className="text-[#E5E5E5]" title="Schedule only applies to un-sent proposals">—</span>;
  }

  const save = async (clear = false) => {
    setBusy(true);
    try {
      const iso = clear ? null : (val ? new Date(val).toISOString() : null);
      await api.patch(`/quotes/${q.quote_id}/scheduled-send`, { scheduled_send_at: iso });
      setEditing(false);
      onChange?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to schedule");
    }
    setBusy(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`scheduled-send-edit-${q.quote_id}`}>
        <input
          type="datetime-local"
          value={val}
          onChange={e => setVal(e.target.value)}
          min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
          className="border border-[#E5E5E5] px-1.5 py-0.5 text-[11px] font-mono"
          data-testid={`scheduled-send-input-${q.quote_id}`}
        />
        <button onClick={() => save(false)} disabled={busy || !val}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-[#0055FF] text-white disabled:opacity-50"
          data-testid={`scheduled-send-save-${q.quote_id}`}>
          {busy ? "…" : "Save"}
        </button>
        <button onClick={() => setEditing(false)}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-[#E5E5E5]"
          data-testid={`scheduled-send-cancel-${q.quote_id}`}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 group" title="Schedule this proposal to send automatically at a specific time">
      {q.scheduled_send_at ? (
        <div className="flex flex-col">
          <span className="text-[#0055FF] font-medium">⏰ {formatTzDate(q.scheduled_send_at, resolveTz(user))}</span>
          <span className="text-[10px] text-[#0055FF] font-mono">
            {formatTz(q.scheduled_send_at, resolveTz(user), { year: undefined, month: undefined, day: undefined })}
            <button
              onClick={() => save(true)}
              className="ml-2 text-[10px] text-[#DC2626] hover:underline"
              data-testid={`scheduled-send-clear-${q.quote_id}`}
            >
              clear
            </button>
          </span>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="text-[11px] text-[#0055FF] hover:underline"
          data-testid={`scheduled-send-add-${q.quote_id}`}
        >
          + Schedule send
        </button>
      )}
      {q.scheduled_send_at && (
        <button
          onClick={() => setEditing(true)}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-[#0055FF] hover:underline ml-1"
          data-testid={`scheduled-send-edit-btn-${q.quote_id}`}
        >
          edit
        </button>
      )}
    </div>
  );
}

export default function QuotesList() {
  const { user, canSee } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [invoiceFilter, setInvoiceFilter] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clientId = searchParams.get("client_id");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (clientId) params.set("client_id", clientId);
    api.get(`/quotes?${params}`).then(r => {
      setQuotes(enrichQuotes(r.data));
    }).finally(() => setLoading(false));
  }, [status, clientId]);

  const { sorted: sortedAll, sortKey, sortDir, onSort } = useSortable(quotes, "heat_rank", "asc");
  const sorted = invoiceFilter ? sortedAll.filter(q => matchesInvoiceFilter(q.invoice, invoiceFilter)) : sortedAll;

  if (user?.user_type === "internal" && !canSee("sales")) return <AccessDenied module="Proposals" />;

  return (
    <div className="p-6" data-testid="quotes-list-page">
      <div className="flex items-center justify-start gap-6 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight">Proposals</h1>
          <p className="text-sm text-[#A3A3A3] mt-0.5">{quotes.length} proposal{quotes.length !== 1 ? "s" : ""}</p>
        </div>
        <Link to="/quotes/new" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB]" data-testid="new-quote-button">
          <Plus size={15} weight="bold" /> New Proposal
        </Link>
        <Link to="/quotes/new?type=adhoc" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E5E5] text-[#0A0A0A] text-sm font-bold uppercase tracking-wider hover:bg-[#F8FAFC] hover:border-[#0055FF]" data-testid="new-adhoc-quote-button" title="For existing clients: custom/ad-hoc work. No contract term, no Year-1 / ongoing-annual totals.">
          <Plus size={15} weight="bold" /> New Ad-hoc Quote
        </Link>
      </div>

      <div className="flex gap-3 mb-4">
        <select value={status} onChange={e => setStatus(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]" data-testid="quotes-status-filter">
          <option value="">All Statuses</option>
          {STATUSES.filter(Boolean).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ")}</option>)}
        </select>
        <select value={invoiceFilter} onChange={e => setInvoiceFilter(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]" data-testid="quotes-invoice-filter" title="Filter by the linked invoice / payment record. Proposal status is filtered separately.">
          {INVOICE_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm" data-testid="quotes-table">
          <thead>
            <tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
              <SortTh sortKey="heat_rank" label="Heat" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="quote_number" label="Proposal #" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="recipient_name" label="Recipient" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="quote_date" label="Date" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="expiry_date" label="Expiry" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="display_total" label={<span className="inline-flex flex-col items-end leading-none"><span>Total</span><span className="text-[9px] font-normal text-[#A3A3A3] normal-case mt-0.5">Year 1</span></span>} current={sortKey} dir={sortDir} onSort={onSort} align="right" />
              <SortTh sortKey="status" label="Status" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="invoice_sort" label="Invoiced" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="payment_sort" label="Paid" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="last_reminder_at" label="Last Reminder" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="next_reminder_at" label="Next Reminder" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="email_first_opened_at" label="Opened" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="version_number" label="Version" current={sortKey} dir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F5F5F5]">
            {loading ? (
              [...Array(3)].map((_, i) => <tr key={i}>{[...Array(13)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-[#F5F5F5] animate-pulse" /></td>)}</tr>)
            ) : sorted.length === 0 ? (
              <tr><td colSpan={13} className="px-4 py-12 text-center text-[#A3A3A3]">No proposals found</td></tr>
            ) : (
              sorted.map(q => (
                <tr key={q.quote_id} className="hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                    onClick={() => navigate(`/quotes/${q.quote_id}`)} data-testid={`quote-row-${q.quote_id}`}>
                  <td className="px-4 py-3"><HeatChip heat={q.heat} /></td>
                  <td className="px-4 py-3 font-mono font-semibold text-[#0A0A0A]">
                    {q.quote_number}
                    {q.quote_type === "adhoc" && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] align-middle"
                            data-testid={`adhoc-tag-${q.quote_id}`}>
                        Ad-hoc
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`quote-recipient-${q.quote_id}`}>
                    <div className="text-[#525252]">{q.recipient_name || "-"}</div>
                    {q.billing_email && (
                      <div className="text-[10px] text-[#A3A3A3] mt-0.5 font-mono lowercase truncate max-w-[280px]" title={q.billing_email}>
                        {q.billing_email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#A3A3A3] text-xs font-mono whitespace-nowrap">{q.quote_date}</td>
                  <td className="px-4 py-3 text-[#A3A3A3] text-xs font-mono whitespace-nowrap">{q.expiry_date || "-"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-[#0A0A0A]" title={totalTooltip(q)} data-testid={`quote-total-${q.quote_id}`}>{fmt(q.display_total ?? q.total_amount, q.currency_code)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-bold ${STATUS_COLORS[q.status] || "bg-[#F5F5F5] text-[#525252]"}`}>
                      {q.status?.replace("_", " ")}
                    </span>
                    {q.approval_state === "pending" && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E]" data-testid={`approval-pending-${q.quote_id}`}>
                        Awaiting Approval
                      </span>
                    )}
                    {q.approval_state === "approved" && q.status === "draft" && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#F0FDF4] text-[#166534]">
                        Approved
                      </span>
                    )}
                    {q.approval_state === "rejected" && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#FEF2F2] text-[#991B1B]">
                        Rejected
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <InvoiceStateCell cell={invoicedCell(q.invoice)} testId={`quote-invoiced-${q.quote_id}`} />
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <InvoiceStateCell cell={paidCell(q.invoice)} testId={`quote-paid-${q.quote_id}`} />
                  </td>
                  <td className="px-4 py-3 text-xs" data-testid={`quote-last-reminder-${q.quote_id}`}>
                    {(q.sent_at || q.last_reminder_at || q.reminders_sent) ? (
                      q.last_reminder_at ? (
                        <div className="flex flex-col" title={`Reminder ${q.reminders_sent || 0} of 9 sent`}>
                          <span className="text-[#525252]">{formatTzDate(q.last_reminder_at, resolveTz(user))}</span>
                          <span className="text-[10px] text-[#A3A3A3] font-mono">
                            {q.reminders_sent ? `${q.reminders_sent} of 9 sent` : ""}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[#A3A3A3] text-[11px]">No reminders yet</span>
                      )
                    ) : (
                      <span className="text-[#A3A3A3] text-[11px] italic" title="Proposal hasn't been sent yet — reminders only apply after the first send">
                        Not sent yet
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs" data-testid={`quote-next-reminder-${q.quote_id}`} onClick={(e) => e.stopPropagation()}>
                    {(q.sent_at || q.last_reminder_at || q.reminders_sent) ? (
                      <NextReminderCell q={q} user={user} onChange={() => {
                        api.get(`/quotes?${status ? `status=${status}` : ""}${clientId ? `&client_id=${clientId}` : ""}`).then(r => {
                          setQuotes(enrichQuotes(r.data));
                        });
                      }} />
                    ) : (
                      <ScheduledSendCell q={q} user={user} onChange={() => {
                        api.get(`/quotes?${status ? `status=${status}` : ""}${clientId ? `&client_id=${clientId}` : ""}`).then(r => {
                          setQuotes(enrichQuotes(r.data));
                        });
                      }} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs" data-testid={`quote-opened-${q.quote_id}`}>
                    {q.email_first_opened_at ? (
                      <div className="flex flex-col" title={`Opened ${q.email_open_count || 1} time(s); last ${q.email_last_opened_at ? formatTz(q.email_last_opened_at, resolveTz(user)) : ""}`}>
                        <span className="text-emerald-700 font-semibold">{formatTzDate(q.email_first_opened_at, resolveTz(user))}</span>
                        <span className="text-[10px] text-[#A3A3A3] font-mono">{formatTz(q.email_first_opened_at, resolveTz(user), { year: undefined, month: undefined, day: undefined })}{q.email_open_count > 1 ? ` · ${q.email_open_count}×` : ""}</span>
                      </div>
                    ) : q.email_last_sent_at ? (
                      <span className="text-[#A3A3A3]">Not yet</span>
                    ) : (
                      <span className="text-[#E5E5E5]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#A3A3A3]">v{q.version_number || 1}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
